import { z } from 'zod';
import {
  ESTADO_CUENTA_EXPORT_FORMAT,
  ESTADO_CUENTA_MOVEMENT_TYPE,
  ESTADO_CUENTA_SERIE_MENSUAL,
} from './types';
import {
  CLIENT_DEACTIVATION_CURRENT_PERIOD_TREATMENT as CURRENT_PERIOD_TREATMENT,
  CLIENT_DEACTIVATION_CUTOFF_STRATEGY as CUTOFF_STRATEGY,
} from './deactivacion';

/** Fecha ISO YYYY-MM-DD (sin hora). null limpia el campo en la API. */
const fechaISO = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'fecha inválida: use el formato YYYY-MM-DD');

const EstadoCuentaFiltersSchema = z.object({
  fechaDesde: fechaISO.optional(),
  fechaHasta: fechaISO.optional(),
  tipo: z
    .enum([
      ESTADO_CUENTA_MOVEMENT_TYPE.CARGO,
      ESTADO_CUENTA_MOVEMENT_TYPE.PAGO,
      ESTADO_CUENTA_MOVEMENT_TYPE.PREPAGO,
      ESTADO_CUENTA_MOVEMENT_TYPE.CREDITO_GENERADO,
      ESTADO_CUENTA_MOVEMENT_TYPE.CREDITO_APLICADO,
      ESTADO_CUENTA_MOVEMENT_TYPE.AJUSTE,
      ESTADO_CUENTA_MOVEMENT_TYPE.ANULACION,
      ESTADO_CUENTA_MOVEMENT_TYPE.REVERSO,
      ESTADO_CUENTA_MOVEMENT_TYPE.RECONCILIACION,
    ])
    .optional(),
  estado: z.string().trim().min(1).max(80).optional(),
  search: z.string().trim().max(120).optional(),
});

function hasValidEstadoCuentaDateRange(value: {
  fechaDesde?: string;
  fechaHasta?: string;
}): boolean {
  return (
    value.fechaDesde === undefined ||
    value.fechaHasta === undefined ||
    value.fechaDesde <= value.fechaHasta
  );
}

const ESTADO_CUENTA_DATE_RANGE_ERROR = {
  path: ['fechaHasta'],
  message: 'fechaHasta must be on or after fechaDesde',
};

/** Converts blank UI text values into an explicit null without changing nonblank values. */
export function blankStringToNull<T>(value: T): T | null {
  return typeof value === 'string' && value.trim() === '' ? null : value;
}

function optionalClientText(schema: z.ZodString) {
  return z.preprocess(blankStringToNull, schema.nullable().optional());
}

export const CreateClienteDtoSchema = z.object({
  nombre: z.string().min(1),
  fechaNacimiento: fechaISO.nullable().optional(),
  identificacion: optionalClientText(z.string()),
  telefonoPrincipal: optionalClientText(z.string()),
  telefonoReferencia: optionalClientText(z.string()),
  email: optionalClientText(z.string().email()),
  ip: optionalClientText(z.string()),
  tipoCliente: z.coerce.number().int().optional(),
  // El estado siempre va definido al crear (default de negocio: ACTIVO).
  estadoCliente: z.coerce.number().int().min(1),
  nit: optionalClientText(z.string()),
  ubicacion: optionalClientText(z.string()),
  comentarios: optionalClientText(z.string()),
  fechaCobro: fechaISO,
  diaCobro: z.coerce.number().int().min(1).max(31).optional(),
  fechaAlta: fechaISO.nullable().optional(),
});

export type CreateClienteDto = z.infer<typeof CreateClienteDtoSchema>;

export const UpdateClienteDtoSchema = z.object({
  nombre: z.string().optional(),
  fechaNacimiento: fechaISO.nullable().optional(),
  identificacion: optionalClientText(z.string()),
  telefonoPrincipal: optionalClientText(z.string()),
  telefonoReferencia: optionalClientText(z.string()),
  email: optionalClientText(z.string().email()),
  ip: optionalClientText(z.string()),
  tipoCliente: z.coerce.number().int().optional(),
  estadoCliente: z.coerce.number().int().min(1).optional(),
  nit: optionalClientText(z.string()),
  ubicacion: optionalClientText(z.string()),
  comentarios: optionalClientText(z.string()),
  // fechaCobro NO es nullable: la columna es NOT NULL (no se puede limpiar).
  fechaCobro: fechaISO.optional(),
  diaCobro: z.coerce.number().int().min(1).max(31).optional(),
  fechaAlta: fechaISO.nullable().optional(),
});

export type UpdateClienteDto = z.infer<typeof UpdateClienteDtoSchema>;

export const QueryClientesDtoSchema = z.object({
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  estado: z.coerce.number().int().optional(),
  tipo: z.coerce.number().int().optional(),
});

export type QueryClientesDto = z.infer<typeof QueryClientesDtoSchema>;

export const QueryEstadoCuentaDtoSchema = EstadoCuentaFiltersSchema.extend({
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(100).default(20),
}).refine(hasValidEstadoCuentaDateRange, ESTADO_CUENTA_DATE_RANGE_ERROR);

export type QueryEstadoCuentaDto = z.infer<typeof QueryEstadoCuentaDtoSchema>;

/** Export query deliberately omits pagination while retaining the screen filters. */
export const EstadoCuentaExportQuerySchema = EstadoCuentaFiltersSchema.extend({
  format: z.enum([ESTADO_CUENTA_EXPORT_FORMAT.PDF, ESTADO_CUENTA_EXPORT_FORMAT.XLSX]),
}).refine(hasValidEstadoCuentaDateRange, ESTADO_CUENTA_DATE_RANGE_ERROR);

export type EstadoCuentaExportQuery = z.infer<typeof EstadoCuentaExportQuerySchema>;
export type EstadoCuentaExportFilters = Omit<EstadoCuentaExportQuery, 'format'>;

/** Query parameters for the exact calendar-month financial-history projection. */
export const QueryEstadoCuentaSerieMensualDtoSchema = z.object({
  meses: z.coerce
    .number()
    .int()
    .min(1)
    .max(ESTADO_CUENTA_SERIE_MENSUAL.MAX_MONTHS)
    .default(ESTADO_CUENTA_SERIE_MENSUAL.DEFAULT_MONTHS),
});

export type QueryEstadoCuentaSerieMensualDto = z.infer<
  typeof QueryEstadoCuentaSerieMensualDtoSchema
>;

const deactivationOperationKey = z.string().trim().min(1).max(160);
const deactivationPreviewHash = z.string().regex(/^[a-f0-9]{64}$/i, 'previewHash must be SHA-256');

export const PreviewClientDeactivationDtoSchema = z.object({
  reason: z.string().trim().min(1).max(500),
  cutoffStrategy: z.enum([CUTOFF_STRATEGY.IMMEDIATE, CUTOFF_STRATEGY.END_OF_PERIOD]).optional(),
  currentPeriodTreatment: z
    .enum([
      CURRENT_PERIOD_TREATMENT.CHARGE_SERVED_DAYS,
      CURRENT_PERIOD_TREATMENT.FULL_PERIOD,
      CURRENT_PERIOD_TREATMENT.FORGIVE_CURRENT_PERIOD,
    ])
    .optional(),
  operationKey: deactivationOperationKey,
  effectiveDate: fechaISO.optional(),
});

export type PreviewClientDeactivationDto = z.infer<typeof PreviewClientDeactivationDtoSchema>;

export const ConfirmClientDeactivationDtoSchema = z.object({
  reason: z.string().trim().min(1).max(500),
  cutoffStrategy: z.enum([CUTOFF_STRATEGY.IMMEDIATE, CUTOFF_STRATEGY.END_OF_PERIOD]),
  currentPeriodTreatment: z.enum([
    CURRENT_PERIOD_TREATMENT.CHARGE_SERVED_DAYS,
    CURRENT_PERIOD_TREATMENT.FULL_PERIOD,
    CURRENT_PERIOD_TREATMENT.FORGIVE_CURRENT_PERIOD,
  ]),
  operationKey: deactivationOperationKey,
  effectiveDate: fechaISO,
  previewHash: deactivationPreviewHash,
});

export type ConfirmClientDeactivationDto = z.infer<typeof ConfirmClientDeactivationDtoSchema>;
